import React, { useState, useEffect } from 'react';
import { X, Building, Search, ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';
import financeService from '@/services/financeService';

const SubsidiaryBreakdownModal = ({ isOpen, onClose }) => {
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'subsidiary', direction: 'asc' });

  useEffect(() => {
    if (isOpen) {
      fetchBreakdown();
    }
  }, [isOpen]);

  const fetchBreakdown = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeService.getAPARSubsidiaryBreakdown();
      
      if (response.data.success) {
        setBreakdown(response.data.data.breakdown || []);
      }
    } catch (err) {
      console.error('Error fetching subsidiary breakdown:', err);
      setError('Failed to load subsidiary breakdown');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return '₦' + new Intl.NumberFormat('en-NG', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    let filteredData = breakdown;

    // Apply search filter
    if (searchTerm) {
      filteredData = filteredData.filter(item =>
        item.subsidiary.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    const sorted = [...filteredData].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'subsidiary':
          aValue = a.subsidiary;
          bValue = b.subsidiary;
          break;
        case 'totalPayables':
          aValue = parseFloat(a.payables.total_amount);
          bValue = parseFloat(b.payables.total_amount);
          break;
        case 'outstandingPayables':
          aValue = parseFloat(a.payables.outstanding_amount);
          bValue = parseFloat(b.payables.outstanding_amount);
          break;
        case 'totalReceivables':
          aValue = parseFloat(a.receivables.total_amount);
          bValue = parseFloat(b.receivables.total_amount);
          break;
        case 'outstandingReceivables':
          aValue = parseFloat(a.receivables.outstanding_amount);
          bValue = parseFloat(b.receivables.outstanding_amount);
          break;
        case 'netPosition':
          aValue = parseFloat(a.net_position);
          bValue = parseFloat(b.net_position);
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? (
      <TrendingUp className="h-4 w-4 text-blue-600" />
    ) : (
      <TrendingDown className="h-4 w-4 text-blue-600" />
    );
  };

  if (!isOpen) return null;

  const sortedData = getSortedData();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Building className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Subsidiary Breakdown</h3>
                <p className="text-blue-100 mt-1">
                  Accounts Payable & Receivable by Subsidiary
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-200" />
              <input
                type="text"
                placeholder="Search subsidiaries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700">{error}</p>
              <button
                onClick={fetchBreakdown}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-12">
              <Building className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm ? 'No subsidiaries found matching your search' : 'No subsidiary data available'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b-2 border-gray-200">
                    <th
                      onClick={() => handleSort('subsidiary')}
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <span>Subsidiary</span>
                        <SortIcon columnKey="subsidiary" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('totalPayables')}
                      className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-end space-x-2">
                        <span>Total Payables</span>
                        <SortIcon columnKey="totalPayables" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('outstandingPayables')}
                      className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-end space-x-2">
                        <span>Outstanding Payables</span>
                        <SortIcon columnKey="outstandingPayables" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('totalReceivables')}
                      className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-end space-x-2">
                        <span>Total Receivables</span>
                        <SortIcon columnKey="totalReceivables" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('outstandingReceivables')}
                      className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-end space-x-2">
                        <span>Outstanding Receivables</span>
                        <SortIcon columnKey="outstandingReceivables" />
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('netPosition')}
                      className="px-4 py-3 text-right text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center justify-end space-x-2">
                        <span>Net Position</span>
                        <SortIcon columnKey="netPosition" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, index) => {
                    const netPosition = parseFloat(item.net_position);
                    const isPositive = netPosition >= 0;

                    return (
                      <tr
                        key={item.subsidiary}
                        className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold">
                              {item.subsidiary.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{item.subsidiary}</p>
                              <p className="text-xs text-gray-500">
                                {item.payables.count + item.receivables.count} total records
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(item.payables.total_amount)}
                          </p>
                          <p className="text-xs text-gray-500">{item.payables.count} items</p>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <p className="font-semibold text-red-600">
                            {formatCurrency(item.payables.outstanding_amount)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Paid: {formatCurrency(item.payables.paid_amount)}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(item.receivables.total_amount)}
                          </p>
                          <p className="text-xs text-gray-500">{item.receivables.count} items</p>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <p className="font-semibold text-green-600">
                            {formatCurrency(item.receivables.outstanding_amount)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Received: {formatCurrency(item.receivables.received_amount)}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-full ${
                            isPositive ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {isPositive ? (
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-600" />
                            )}
                            <span className={`font-bold ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                              {formatCurrency(Math.abs(netPosition))}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {sortedData.length} {sortedData.length === 1 ? 'subsidiary' : 'subsidiaries'}
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubsidiaryBreakdownModal;
